import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { fetchRestaurantFeeHistory } from "../../../../redux/thunk";
import { FeeHistory as FeeHistoryType } from "../types";
import { formatDate, getChangeTypeBadgeColor, getStatusBadgeColor } from "../mockData";
import LoadingSpinner from "../../../../components/elements/LoadingSpinner";
import { HiOutlineDownload, HiOutlineEye } from "react-icons/hi";

const FeeHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const { feeHistory, loading, error } = useAppSelector((state) => state.fees);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<'all' | 'commission' | 'delivery' | 'service' | 'global'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'reverted'>('all');
  const [filteredHistory, setFilteredHistory] = useState<FeeHistoryType[]>([]);

  useEffect(() => {
    dispatch(fetchRestaurantFeeHistory());
  }, [dispatch]);

  useEffect(() => {
    if (feeHistory) {
      let filtered = feeHistory.filter((record) => {
        const matchesSearch = searchTerm === '' || 
          (record.restaurantName && record.restaurantName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          record.changedBy.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = filterType === 'all' || record.changeType === filterType;
        const matchesStatus = filterStatus === 'all' || record.status === filterStatus;

        return matchesSearch && matchesType && matchesStatus;
      });

      // Sort by timestamp (most recent first)
      filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      setFilteredHistory(filtered);
    }
  }, [feeHistory, searchTerm, filterType, filterStatus]);

  const handleExportHistory = () => {
    // This would trigger a CSV or PDF export
    console.log("Export history triggered");
  };

  const handleViewDetails = (recordId: string) => {
    // This would open a modal with detailed view
    console.log(`View details for record: ${recordId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters and Export */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">Fee Change History</h3>
          <p className="text-sm text-gray-500">
            Track all changes made to commission rates, delivery fees, and service charges
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleExportHistory}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-md hover:bg-secondary-dark transition-colors"
          >
            <HiOutlineDownload className="w-4 h-4" />
            Export History
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Search</label>
            <input
              type="text"
              placeholder="Search by restaurant or admin..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Change Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="all">All Types</option>
              <option value="commission">Commission</option>
              <option value="delivery">Delivery</option>
              <option value="service">Service</option>
              <option value="global">Global</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="reverted">Reverted</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('all');
                setFilterStatus('all');
              }}
              className="w-full px-4 py-2 border rounded-md hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* History Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Change Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Restaurant
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Previous Value
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  New Value
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Changed By
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHistory.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChangeTypeBadgeColor(record.changeType)}`}>
                      {record.changeType.charAt(0).toUpperCase() + record.changeType.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {record.restaurantName || 'Global Settings'}
                      </div>
                      {record.restaurantId && (
                        <div className="text-sm text-gray-500">{record.restaurantId}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs">
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify(record.previousValue, null, 2)}
                      </pre>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs">
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify(record.newValue, null, 2)}
                      </pre>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{record.changedBy}</div>
                    <div className="text-sm text-gray-500">{record.changeReason}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(record.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(record.status)}`}>
                      {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(record.id)}
                      className="flex items-center gap-2 text-secondary hover:text-secondary-dark"
                    >
                      <HiOutlineEye className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredHistory.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
                ? 'No fee changes found matching your filters.'
                : 'No fee changes recorded yet.'
              }
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Total Changes</h4>
          <p className="text-2xl font-bold text-gray-900">{feeHistory.length}</p>
        </div>
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Commission Changes</h4>
          <p className="text-2xl font-bold text-gray-900">
            {feeHistory.filter(h => h.changeType === 'commission').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Delivery Changes</h4>
          <p className="text-2xl font-bold text-gray-900">
            {feeHistory.filter(h => h.changeType === 'delivery').length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-500">Reverted Changes</h4>
          <p className="text-2xl font-bold text-gray-900">
            {feeHistory.filter(h => h.status === 'reverted').length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeeHistory;